// PRPricing.js - Portugal
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const PRPricing = () => {
  const coverageItems = [
    'An eligibility assessment, with a recommendation on the right route',
    'A look at the investment options and which of them suits your position',
    'Preparation and verification of your documents',
    'Completion and review of the application forms',
    'Liaison with lawyers and investment advisers on your behalf',
    'Help obtaining a NIF and opening a Portuguese bank account',
    'Booking your SEF appointment and preparing you for it',
    'Continued support once the application has gone in'
  ];

  const importantNotes = [
    'Application fees charged by the state go straight to the Portuguese authorities (SEF)',
    'Investment sums of €250,000-€500,000 are paid directly into the investment vehicle itself',
    'Legal fees for preparing investment documentation are billed separately',
    'Where a property transaction is involved, its costs come on top',
    'Residency is decided by the Portuguese immigration authority, never by Ivipass. Our part is shaping the file around what that authority actually asks for and evidencing every element of it. We do not promise an outcome, and you should be wary of anyone who does.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Our Fees for Portugal
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6 border border-gray-200"
          >
            <h3 className="text-xl font-semibold text-primary mb-4">
              Portuguese Residency Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              A single service that carries your Portuguese residency application from the opening assessment through to the decision.
            </p>

            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Here is what you get:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-primary mb-4">
                Service Fee
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-primary">Full Residency Support</p>
                    <p className="text-sm text-gray-600">Everything from preparation through to submission</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">$399</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> this covers our professional work on the application itself. Investment sums, legal fees and government charges all sit outside it.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              The final figure is agreed at the end of your consultation, once we know which route you are taking and what it involves.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-primary">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6"
          >
            <h4 className="text-base font-semibold text-primary mb-3">
              Government Fees, Payable Direct to SEF
            </h4>
            <div className="space-y-2 text-sm text-gray-700 font-light">
              <p>• <strong>Golden Visa application:</strong> €533.90 for the main applicant</p>
              <p>• <strong>Golden Visa renewal:</strong> €2,669.60 every 2 years</p>
              <p>• <strong>D7 visa application:</strong> €83, charged at the consulate</p>
              <p>• <strong>Residence permit card:</strong> €72.40</p>
              <p>• <strong>Family members:</strong> charged per person on top</p>
              <p className="text-xs text-gray-600 mt-3">
                These charges are revised periodically. Investment sums of €250,000-€500,000 are separate again, and go straight to the investment vehicle.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PRPricing;
